import { useState, type ChangeEvent, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../auth/AuthContext';
import { api, type FeedbackImportance } from '../api/client';
import { downscaleToJpeg } from '../lib/image';
import type { Pose } from '../types';

const IMPORTANCE: FeedbackImportance[] = ['Low', 'Nice-to-have', 'Important'];
const MAX_IMAGES = 4;

/**
 * "Suggest an improvement" trigger. Signed-in only (submissions are tied to an
 * account so we can follow up), so it renders nothing for guests. `pose`
 * pre-fills the context when the request is about a specific pose.
 */
export function SuggestButton({
  pose,
  label = 'Suggest an improvement',
  className = 'linklike',
}: {
  pose?: Pose;
  label?: string;
  className?: string;
}) {
  const { status } = useAuth();
  const [open, setOpen] = useState(false);
  if (status !== 'authenticated') return null;
  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        {label}
      </button>
      {open && <FeedbackModal pose={pose} onClose={() => setOpen(false)} />}
    </>
  );
}

function FeedbackModal({ pose, onClose }: { pose?: Pose; onClose: () => void }) {
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');
  const [importance, setImportance] = useState<FeedbackImportance>('Nice-to-have');
  const [images, setImages] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const context = pose
    ? `Pose: ${pose.nameEnglish} (${pose.id})`
    : window.location.hash || 'General';

  async function addFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // allow re-picking the same file
    setError(null);
    for (const file of files) {
      if (images.length >= MAX_IMAGES) break;
      try {
        const jpeg = await downscaleToJpeg(file, 1400);
        setImages((prev) => (prev.length < MAX_IMAGES ? [...prev, jpeg] : prev));
      } catch {
        setError('Could not read one of those images.');
      }
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.submitFeedback({
        summary: summary.trim(),
        details: details.trim(),
        importance,
        context,
        images,
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your request.');
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal fb-card"
        role="dialog"
        aria-modal="true"
        aria-label="Suggest an improvement"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        {done ? (
          <div className="fb-done">
            <h2 className="auth-title">Thank you!</h2>
            <p className="auth-lede">
              Your request was sent{pose ? ` about ${pose.nameEnglish}` : ''}. We read every one.
            </p>
            <button className="btn primary" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <h2 className="auth-title">Suggest an improvement</h2>
            <p className="auth-lede">
              {pose
                ? `See something off with ${pose.nameEnglish} — its art or description? Tell us.`
                : 'Spot something we could improve — artwork, a description, anything? Tell us.'}
            </p>

            <form className="auth-form" onSubmit={handleSubmit}>
              <label>
                Summary
                <input
                  type="text"
                  required
                  maxLength={200}
                  placeholder="A short title for your request"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                />
              </label>

              <label>
                Details
                <textarea
                  className="fb-details"
                  rows={4}
                  maxLength={4000}
                  placeholder="What would you improve, and why?"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                />
              </label>

              <div className="fb-field">
                <span className="fb-label">Importance</span>
                <div className="gen-chips">
                  {IMPORTANCE.map((level) => (
                    <button
                      key={level}
                      type="button"
                      className={`chip${importance === level ? ' on' : ''}`}
                      onClick={() => setImportance(level)}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              <div className="fb-field">
                <span className="fb-label">
                  Images <span className="fb-optional">(optional, up to {MAX_IMAGES})</span>
                </span>
                {images.length > 0 && (
                  <div className="fb-thumbs">
                    {images.map((src, i) => (
                      <div className="fb-thumb" key={i}>
                        <img src={src} alt={`Attachment ${i + 1}`} />
                        <button
                          type="button"
                          className="fb-thumb-x"
                          aria-label="Remove image"
                          onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {images.length < MAX_IMAGES && (
                  <label className="fb-add">
                    + Add image
                    <input type="file" accept="image/*" multiple hidden onChange={addFiles} />
                  </label>
                )}
              </div>

              <p className="fb-context">Attached context: {context}</p>

              {error && <p className="auth-error">{error}</p>}

              <button className="btn primary auth-submit" type="submit" disabled={busy || !summary.trim()}>
                {busy ? 'Sending…' : 'Send request'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
